import { Injectable, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom, timeout } from 'rxjs';
import { ApiService } from './api.service';
import { AuthToken, User } from '../models/domain.models';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly api = inject(ApiService);
  private readonly router = inject(Router);

  private readonly _user = signal<User | null>(null);
  private readonly _loading = signal(false);
  private readonly _token = signal<string | null>(localStorage.getItem('access_token'));

  user = this._user.asReadonly();
  loading = this._loading.asReadonly();
  isAuthenticated = computed(() => !!this._user());

  async hydrate(): Promise<void> {
    const token = this._token();
    if (!token) {
      this._user.set(null);
      return;
    }
    this._loading.set(true);
    try {
      const me = await firstValueFrom(this.api.get<User>('/api/auth/me').pipe(timeout(8000)));
      this._user.set(me);
    } catch {
      this.logout(false);
    } finally {
      this._loading.set(false);
    }
  }

  async login(username: string, password: string): Promise<void> {
    this._loading.set(true);
    try {
      const token = await firstValueFrom(
        this.api.post<AuthToken>('/api/auth/login', { username, password }),
      );
      this._token.set(token.access_token);
      localStorage.setItem('access_token', token.access_token);
      this._user.set(token.user);
      void this.router.navigateByUrl(this.defaultRouteForUser(token.user));
    } finally {
      this._loading.set(false);
    }
  }

  logout(redirect = true): void {
    this.api.post('/api/auth/logout', {}).subscribe({ error: () => undefined });
    this._token.set(null);
    this._user.set(null);
    localStorage.removeItem('access_token');
    if (redirect) {
      this.router.navigateByUrl('/login');
    }
  }

  private defaultRouteForUser(user: User): string {
    if (user.role === 'vendor' && user.vendor_id) {
      return `/vendors/${user.vendor_id}`;
    }
    return '/';
  }
}
